import { ShaderModule, ShaderRenderContext } from './index';

/**
 * Temporal Freeze Shader Mode
 *
 * Simulates the Caesura: the unrendered, motionless gap between consecutive
 * Planck-moments of time (t = 0).
 * Features:
 * - Unrendered stillness stasis with near-zero effective velocity
 * - Breathing void halo radiating cosmic inhalation/exhalation cycles
 * - Chromatic aberration fringe rings on the halo perimeter
 * - Frozen crystalline dust suspended motionless with infinitesimal quantum jitter
 * - Geometric stillness reticles and stasis caliper marks
 */
export class TemporalFreezeShader implements ShaderModule {
  readonly name = 'temporal_freeze' as const;

  // Stasis shards: fixed coordinates initialized once
  private shards: Array<{ xRel: number; yRel: number; size: number; baseAlpha: number; phase: number }> | null = null;

  private initShards(): void {
    this.shards = [];
    const count = 120;
    for (let i = 0; i < count; i++) {
      this.shards.push({
        xRel: (Math.random() - 0.5) * 1.8, // [-0.9, 0.9] of radius
        yRel: (Math.random() - 0.5) * 1.8,
        size: 0.8 + Math.random() * 2.4,
        baseAlpha: 0.2 + Math.random() * 0.7,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  render(context: ShaderRenderContext): void {
    const { ctx, width, height, time, uniforms, reducedMotion } = context;
    const { speed, intensity, distortion, gridDensity } = uniforms;

    if (!this.shards) {
      this.initShards();
    }

    // Stasis speed: severely throttled to convey the terrifying cessation of time
    const stasisSpeed = reducedMotion ? 0.01 : speed * 0.05;
    const t = time * stasisSpeed;

    const cx = width * 0.5;
    const cy = height * 0.5;
    const minDim = Math.min(width, height);

    // 1. Deep Obsidian Void Layer
    ctx.fillStyle = '#030508';
    ctx.fillRect(0, 0, width, height);

    ctx.save();

    // 2. Breathing Void Halo
    // Low-frequency breathing cycle (sinusoidal inhalation/exhalation)
    const breathRate = 0.6; // slow respiration
    const breathCycle = Math.sin(time * breathRate * (reducedMotion ? 0.2 : 0.8));
    const haloExpansion = 1.0 + breathCycle * (0.12 + distortion * 0.15);
    const baseRadius = minDim * (0.32 + intensity * 0.22);
    const currentRadius = baseRadius * haloExpansion;

    // Outer Void Halo Radial Gradient
    const haloGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, currentRadius);
    haloGrad.addColorStop(0, 'rgba(3, 5, 8, 1.0)');
    haloGrad.addColorStop(0.35, 'rgba(10, 16, 26, 0.95)');
    haloGrad.addColorStop(0.65, `rgba(157, 78, 221, ${0.18 + intensity * 0.25})`);
    haloGrad.addColorStop(0.85, `rgba(0, 243, 255, ${0.12 + intensity * 0.18})`);
    haloGrad.addColorStop(1.0, 'rgba(3, 5, 8, 0.0)');

    ctx.fillStyle = haloGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, currentRadius, 0, Math.PI * 2);
    ctx.fill();

    // 3. Chromatic Aberration Rim Fringes
    // Dual chromatic fringe rings expanding from the breathing halo perimeter
    const chromaOffset = (2.0 + distortion * 8.0) * (reducedMotion ? 0.3 : 1.0);

    // Inner rim: Cyan stasis fringe
    ctx.strokeStyle = `rgba(0, 243, 255, ${0.25 + intensity * 0.45})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cx - chromaOffset, cy, currentRadius * 0.88, 0, Math.PI * 2);
    ctx.stroke();

    // Outer rim: Crimson/Seam-violet stasis fringe
    ctx.strokeStyle = `rgba(255, 42, 75, ${0.18 + distortion * 0.45})`;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cx + chromaOffset, cy, currentRadius * 0.92, 0, Math.PI * 2);
    ctx.stroke();

    // 4. Frozen Crystalline Shards (Suspended in Mid-Air)
    if (this.shards) {
      for (let i = 0; i < this.shards.length; i++) {
        const shard = this.shards[i];

        // Microscopic Heisenberg uncertainty jitter (< 0.3px)
        const jitterX = Math.sin(t * 12 + shard.phase) * (reducedMotion ? 0.05 : 0.28);
        const jitterY = Math.cos(t * 10 + shard.phase) * (reducedMotion ? 0.05 : 0.28);

        const px = cx + shard.xRel * currentRadius * 0.85 + jitterX;
        const py = cy + shard.yRel * currentRadius * 0.85 + jitterY;

        if (px < 0 || px > width || py < 0 || py > height) continue;

        // Frozen shard luminescence
        const shardAlpha = Math.min(
          1,
          shard.baseAlpha * (0.4 + intensity * 0.6) * (0.8 + 0.2 * Math.sin(shard.phase + t * 2))
        );

        // Faceted crystal diamond
        ctx.fillStyle = i % 2 === 0
          ? `rgba(220, 245, 255, ${shardAlpha})`
          : `rgba(157, 78, 221, ${shardAlpha * 0.9})`;

        const s = shard.size;
        ctx.beginPath();
        ctx.moveTo(px, py - s);
        ctx.lineTo(px + s * 0.7, py);
        ctx.lineTo(px, py + s);
        ctx.lineTo(px - s * 0.7, py);
        ctx.closePath();
        ctx.fill();
      }
    }

    // 5. Stasis Geometric Reticles and Calipers
    const reticleAlpha = 0.08 + intensity * 0.16;
    const ringCount = Math.max(2, Math.min(6, Math.round(gridDensity / 14)));

    for (let r = 1; r <= ringCount; r++) {
      const ringRadius = (currentRadius / ringCount) * r;
      ctx.strokeStyle = `rgba(0, 243, 255, ${reticleAlpha * (1 - r / (ringCount + 1))})`;
      ctx.lineWidth = 1.0;
      ctx.setLineDash([4, 8]);
      ctx.beginPath();
      ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.setLineDash([]); // Reset dash

    // Motionless Cardinal Calipers (symbolizing the stopped cosmic clock)
    ctx.strokeStyle = `rgba(157, 78, 221, ${reticleAlpha * 1.5})`;
    ctx.lineWidth = 1.0;
    const caliperLen = currentRadius * 0.95;

    ctx.beginPath();
    ctx.moveTo(cx - caliperLen, cy);
    ctx.lineTo(cx - caliperLen + 18, cy);
    ctx.moveTo(cx + caliperLen - 18, cy);
    ctx.lineTo(cx + caliperLen, cy);
    ctx.moveTo(cx, cy - caliperLen);
    ctx.lineTo(cx, cy - caliperLen + 18);
    ctx.moveTo(cx, cy + caliperLen - 18);
    ctx.lineTo(cx, cy + caliperLen);
    ctx.stroke();

    ctx.restore();
  }
}
